import styled, { DefaultTheme, ThemeProps } from 'styled-components';
import { getResponsiveStyles } from '@optimus-web/breakpoints';
import { getSpacingToken } from '@optimus-web/common';
import { FlexDirection, FlexWrap, StackItemProps, StackProps, StackResponsiveProps } from './types';

const getItemAlignment = (itemAlignment?: StackProps['itemAlignment']) => {
    switch (itemAlignment) {
        case 'end':
            return 'flex-end';
        case 'start':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'baseline':
            return 'baseline';
        default:
            return undefined;
    }
};

const getContentJustify = (alignment?: StackProps['alignment'], distribution?: StackProps['distribution']) => {
    switch (true) {
        case alignment === 'end' && distribution === undefined:
            return 'flex-end';
        case distribution === 'space-between':
            return 'space-between';
        case distribution === 'space-around':
            return 'space-around';
        case alignment === 'center':
            return 'center';
        case alignment === 'start' && distribution === undefined:
        default:
            return 'flex-start';
    }
};

const getStackResponsiveStyles = (props: StackResponsiveProps & ThemeProps<DefaultTheme>) => ({
    justifyContent: getContentJustify(props.alignment, props.distribution),
    ...(props.distribution === 'stretch' && {
        '& > *': {
            flexGrow: 1,
            width: '100%',
        },
    }),
    ...(props.itemAlignment && {
        alignItems: getItemAlignment(props.itemAlignment),
    }),
    ...(props.inline && {
        display: 'inline-flex',
    }),
    ...(props.wrap && {
        flexWrap: 'wrap' as FlexWrap,
    }),
    ...(props.fill && {
        width: '100%',
        height: '100%',
    }),
    ...(props.vertical && {
        flexDirection: 'column' as FlexDirection,
    }),
    ...(props.spacing && {
        gap: getSpacingToken(props.spacing)({ theme: props.theme }) + 'px',
    }),
});

export const Stack = styled('div').withConfig<StackProps>({
    shouldForwardProp: (prop, defaultValidatorFn) =>
        !['fill', 'wrap', 'spacing'].includes(prop) && defaultValidatorFn(prop),
})`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    margin: 0;
    padding: 0;

    ${getStackResponsiveStyles}
    ${getResponsiveStyles(getStackResponsiveStyles)}
`;

const getStackItemResponsiveStyles = (props: StackItemProps & ThemeProps<DefaultTheme>) => ({
    padding: props.padding ? getSpacingToken(props.padding)({ theme: props.theme }) : undefined,
    ...(props.contentFit && {
        flexGrow: 0,
        width: 'auto',
    }),
    ...(props.width && {
        minWidth: props.width,
        flex: `0 0 ${props.width}`,
    }),
    ...(props.grow && {
        flexGrow: 1,
        width: '100%',
    }),
    ...(props.minWidth && {
        minWidth: props.minWidth,
    }),
    ...(props.maxWidth && {
        maxWidth: props.maxWidth,
    }),
});

export const StackItem = styled.div<StackItemProps>`
    box-sizing: border-box;
    ${getStackItemResponsiveStyles}
    ${getResponsiveStyles(getStackItemResponsiveStyles)}
`;
